import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ActionsContext,
  ModalContentContext,
  ModalContext,
} from '../../model/state';
import InvoiceAndTypesForm from '../InvoiceAndTypesForm/InvoiceAndTypesForm';
import TypesAndStateForm from '../TypesAndStateForm/TypesAndStateForm';
import { useCanChangePaymentState } from '../../model/globalState';
import InvoiceModal from '../InvoiceModal/InvoiceModal';
import {
  useModalContentType,
  modalContentTypes,
} from '../../config/modalContent';
import { useSumbit } from '../../api/formSubmit';
import { transformValues } from '../../model/form';

const useValueRef = (value) => {
  const valueRef = useRef(value);
  valueRef.current = value;
  return valueRef;
};

const NullableContent = () => null;

const getModalPropsByContentType = ({ contentType, canChangePaymentType }) => {
  let Content = NullableContent;
  let title = '';

  if (contentType === modalContentTypes.invoiceAndTypes) {
    Content = InvoiceAndTypesForm;
    title = ['Выставить счет', canChangePaymentType && 'и способ оплаты']
      .filter(Boolean)
      .join(' ');
  }

  if (contentType === modalContentTypes.typesAndState) {
    Content = TypesAndStateForm;
    title = 'Установить статус оплаты';
  }

  return {
    Content,
    title,
    transform: transformValues[contentType] || ((values) => values),
  };
};

const BaseProvider = ({ onSuccess, children }) => {
  const canChangePaymentType = useCanChangePaymentState();

  const [entity, setEntity] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalBtnsDisabled, setModalBtnsDisabled] = useState(false);

  const contentType = useModalContentType(entity);
  const modalPropsByContentType = useMemo(
    () => getModalPropsByContentType({ contentType, canChangePaymentType }),
    [contentType, canChangePaymentType]
  );

  const onSuccessRef = useValueRef(onSuccess);

  const mutationData = useSumbit({ contentType, cbRef: onSuccessRef });

  const onModalCancel = useCallback(() => {
    setModalVisible(false);
    setModalBtnsDisabled(false);
  }, []);

  const onInvoiceBlockClick = useCallback((entity) => {
    setEntity(entity);
    setModalVisible(true);
  }, []);

  const onEntityUpdate = useCallback((entity) => {
    if (entity) {
      setEntity(entity);
    }
  }, []);

  const modalProps = useMemo(
    () => ({
      visible: modalVisible,
      afterClose: () => setEntity(null),
      onCancel: onModalCancel,
      btnsDisabled: modalBtnsDisabled,
      requestId: entity?.id,
      ...modalPropsByContentType,
      ...mutationData,
    }),
    [
      modalVisible,
      modalPropsByContentType,
      mutationData,
      entity?.id,
      modalBtnsDisabled,
      onModalCancel,
    ]
  );

  const modalContentProps = useMemo(() => ({ entity }), [entity]);

  const actions = useMemo(
    () => ({
      onInvoiceBlockClick,
      onSuccessRef,
      onVisibleTypesPopoverChange: setModalBtnsDisabled,
      onEntityUpdate,
    }),
    [onInvoiceBlockClick, onEntityUpdate]
  );

  return (
    <ModalContext.Provider value={modalProps}>
      <ModalContentContext.Provider value={modalContentProps}>
        <ActionsContext.Provider value={actions}>
          {children}
        </ActionsContext.Provider>
      </ModalContentContext.Provider>
    </ModalContext.Provider>
  );
};

const Provider = ({ onSuccess, children }) => {
  return (
    <BaseProvider onSuccess={onSuccess}>
      {children}
      <InvoiceModal />
    </BaseProvider>
  );
};

export default Provider;
